import axios from "axios";
import { useEffect, useRef, useState } from "react";

import { DEMO_URL } from "@/constants/links";

// API base for production. Hardcoded so the static build hosted on
// c-ore.in posts the lead to the Emergent FastAPI backend running at
// boardroom.c-ore.in. Override via REACT_APP_BACKEND_URL at build time
// if you ever move the backend.
const BACKEND_URL =
    process.env.REACT_APP_BACKEND_URL || "https://boardroom.c-ore.in";
const API = `${BACKEND_URL}/api`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INITIAL = {
    name: "",
    company: "",
    phone: "",
    email: "",
};

function validate(values) {
    const errors = {};
    if (!values.name || values.name.trim().length < 2) {
        errors.name = "Please enter your full name";
    }
    if (!values.company || values.company.trim().length < 1) {
        errors.company = "Company name is required";
    }
    const phoneDigits = values.phone.replace(/\D/g, "");
    if (phoneDigits.length !== 10) {
        errors.phone = "Enter a valid 10-digit mobile number";
    }
    if (!values.email || !EMAIL_RE.test(values.email.trim())) {
        errors.email = "Enter a valid email address";
    }
    return errors;
}

export default function LeadModal({ open, inquiryType, onClose }) {
    const [values, setValues] = useState(INITIAL);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [phoneFocused, setPhoneFocused] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const firstFieldRef = useRef(null);

    const isBookCall = inquiryType === "demo_call";

    // Reset state whenever modal is opened.
    useEffect(() => {
        if (open) {
            setValues(INITIAL);
            setErrors({});
            setSubmitError(null);
            setSubmitting(false);
            // focus first input after the modal renders
            setTimeout(() => firstFieldRef.current?.focus(), 30);
            const onKey = (e) => {
                if (e.key === "Escape") onClose();
            };
            const prevOverflow = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            window.addEventListener("keydown", onKey);
            return () => {
                window.removeEventListener("keydown", onKey);
                document.body.style.overflow = prevOverflow;
            };
        }
    }, [open, onClose]);

    if (!open) return null;

    const setField = (k, v) => {
        setValues((s) => ({ ...s, [k]: v }));
        if (errors[k]) {
            setErrors((e) => {
                const next = { ...e };
                delete next[k];
                return next;
            });
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        const v = {
            ...values,
            name: values.name.trim(),
            company: values.company.trim(),
            email: values.email.trim(),
            phone: values.phone.trim(),
        };
        const errs = validate(v);
        setErrors(errs);
        if (Object.keys(errs).length) return;

        setSubmitting(true);
        setSubmitError(null);

        const payload = {
            name: v.name,
            company: v.company,
            phone: `+91 ${v.phone.replace(/\D/g, "")}`,
            email: v.email,
            inquiry_type: inquiryType,
        };

        const proceedToDemo = () => {
            try {
                sessionStorage.setItem("demo_unlocked", "true");
            } catch (_) {
                /* ignore storage errors */
            }
            window.location.href = DEMO_URL;
        };

        try {
            await axios.post(`${API}/website/inquiry`, payload, {
                timeout: 12000,
            });
            // For book-a-call, show inline confirmation instead of redirect.
            if (isBookCall) {
                setSubmitting(false);
                onClose();
                window.alert(
                    "Thanks! Our team will reach out within one business day.",
                );
                return;
            }
            // For demo view, redirect to demo (the lead is captured).
            proceedToDemo();
        } catch (err) {
            // Never block the demo on a backend hiccup.
            // Lead capture failures are non-blocking for demo viewing,
            // but for "book a demo call" we let the user retry.
            // eslint-disable-next-line no-console
            console.error("inquiry error:", err);
            if (isBookCall) {
                setSubmitError(
                    "Could not send your request. Please try again or WhatsApp us at +91 93718 80369.",
                );
                setSubmitting(false);
                return;
            }
            proceedToDemo();
        }
    };

    return (
        <div
            className="core-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="lead-modal-title"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
            data-testid="lead-modal"
        >
            <div className="core-modal">
                <button
                    type="button"
                    className="core-modal-close"
                    onClick={onClose}
                    aria-label="Close"
                    data-testid="lead-modal-close"
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                        <line x1="5" y1="5" x2="19" y2="19" />
                        <line x1="19" y1="5" x2="5" y2="19" />
                    </svg>
                </button>

                <h3 id="lead-modal-title">
                    {isBookCall
                        ? "Book a demo call"
                        : "See C-ORE C-Suites in action"}
                </h3>
                <p className="lead">
                    {isBookCall
                        ? "Tell us about your business. We'll set up a personalised walkthrough."
                        : "Enter your details to explore a live demo dashboard with sample data."}
                </p>

                <form onSubmit={submit} noValidate>
                    <div className="core-form-field">
                        <label htmlFor="lead-name">Full Name</label>
                        <input
                            id="lead-name"
                            ref={firstFieldRef}
                            type="text"
                            value={values.name}
                            onChange={(e) => setField("name", e.target.value)}
                            className={errors.name ? "is-invalid" : ""}
                            placeholder="Rajesh Sharma"
                            autoComplete="name"
                            data-testid="lead-name"
                        />
                        {errors.name && (
                            <div className="core-form-error" data-testid="lead-name-error">
                                {errors.name}
                            </div>
                        )}
                    </div>
                    <div className="core-form-field">
                        <label htmlFor="lead-company">Company Name</label>
                        <input
                            id="lead-company"
                            type="text"
                            value={values.company}
                            onChange={(e) =>
                                setField("company", e.target.value)
                            }
                            className={errors.company ? "is-invalid" : ""}
                            placeholder="Sharma Steel Fabricators"
                            autoComplete="organization"
                            data-testid="lead-company"
                        />
                        {errors.company && (
                            <div className="core-form-error" data-testid="lead-company-error">
                                {errors.company}
                            </div>
                        )}
                    </div>
                    <div className="core-form-field">
                        <label htmlFor="lead-phone">Phone</label>
                        <div
                            className={`core-phone-wrap ${phoneFocused ? "is-focused" : ""} ${
                                errors.phone ? "is-invalid" : ""
                            }`}
                        >
                            <span className="prefix">+91</span>
                            <input
                                id="lead-phone"
                                type="tel"
                                value={values.phone}
                                onChange={(e) => {
                                    const onlyDigits = e.target.value
                                        .replace(/\D/g, "")
                                        .slice(0, 10);
                                    setField("phone", onlyDigits);
                                }}
                                onFocus={() => setPhoneFocused(true)}
                                onBlur={() => setPhoneFocused(false)}
                                placeholder="9876543210"
                                inputMode="numeric"
                                autoComplete="tel-national"
                                data-testid="lead-phone"
                            />
                        </div>
                        {errors.phone && (
                            <div className="core-form-error" data-testid="lead-phone-error">
                                {errors.phone}
                            </div>
                        )}
                    </div>
                    <div className="core-form-field">
                        <label htmlFor="lead-email">Email</label>
                        <input
                            id="lead-email"
                            type="email"
                            value={values.email}
                            onChange={(e) => setField("email", e.target.value)}
                            className={errors.email ? "is-invalid" : ""}
                            placeholder="you@company.in"
                            autoComplete="email"
                            data-testid="lead-email"
                        />
                        {errors.email && (
                            <div className="core-form-error" data-testid="lead-email-error">
                                {errors.email}
                            </div>
                        )}
                    </div>

                    {submitError && (
                        <div
                            className="core-form-error"
                            style={{ marginTop: 8, marginBottom: 4 }}
                            data-testid="lead-submit-error"
                        >
                            {submitError}
                        </div>
                    )}

                    <button
                        type="submit"
                        className="core-btn core-btn--emerald core-btn--block core-modal-submit"
                        disabled={submitting}
                        data-testid="lead-submit"
                    >
                        {submitting ? (
                            <>
                                <span className="core-loading-dot" />
                                <span className="core-loading-dot" />
                                <span className="core-loading-dot" />
                            </>
                        ) : isBookCall ? (
                            "Request my demo call →"
                        ) : (
                            "View Demo Dashboard →"
                        )}
                    </button>
                </form>

                <p className="core-modal-note">
                    We respect your privacy. Your details are never shared.
                </p>
            </div>
        </div>
    );
}
